import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineCalendarDays,
  HiOutlineCurrencyDollar,
  HiOutlineClock,
  HiOutlineXCircle,
  HiOutlineArchiveBox,
  HiOutlineBanknotes,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
} from "react-icons/hi2";
import { logFutureCommissionFeTrace } from "../../../utils/basicFinancialsKpi";
import { canViewHrCommissionSystemKpi } from "../../../services/authService";
import { getHrCommissionSystemTotal } from "../../../services/fhpService";
import "./DashboardStats.scss";

const HEBREW_MONTHS = [
  "ינואר",
  "פברואר",
  "מרץ",
  "אפריל",
  "מאי",
  "יוני",
  "יולי",
  "אוגוסט",
  "ספטמבר",
  "אוקטובר",
  "נובמבר",
  "דצמבר",
];

function currentMonthKey() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function shiftMonthKey(monthKey, delta) {
  const [yRaw, mRaw] = String(monthKey || currentMonthKey()).split("-");
  const y = Number(yRaw);
  const m = Number(mRaw);
  if (!Number.isFinite(y) || !Number.isFinite(m)) return currentMonthKey();
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function formatMonthLabel(monthKey) {
  const [yRaw, mRaw] = String(monthKey || "").split("-");
  const y = Number(yRaw);
  const m = Number(mRaw);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) {
    return monthKey || "—";
  }
  return `${HEBREW_MONTHS[m - 1]} ${y}`;
}

function formatCurrencyIls(value) {
  return new Intl.NumberFormat("he-IL", {
    style: "currency",
    currency: "ILS",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);
}

const HR_COMMISSION_DEFER_MS = 1800;
/** localStorage: "hr" | "openOrders" — fifth-card slot for allowlisted user only. */
const HR_COMMISSION_KPI_SLOT_KEY = "hrCommissionKpiSlot";

function readHrCommissionKpiSlot() {
  try {
    const raw = String(localStorage.getItem(HR_COMMISSION_KPI_SLOT_KEY) || "").trim();
    if (raw === "openOrders" || raw === "hr") return raw;
    // Legacy hide flag from previous Ctrl/Shift+S hide-only behavior.
    if (localStorage.getItem("hrCommissionKpiHidden") === "1") return "openOrders";
  } catch {
    /* ignore */
  }
  return "hr";
}

function writeHrCommissionKpiSlot(slot) {
  try {
    localStorage.setItem(
      HR_COMMISSION_KPI_SLOT_KEY,
      slot === "openOrders" ? "openOrders" : "hr"
    );
  } catch {
    /* ignore quota / private mode */
  }
}

const HrCommissionKpiCard = ({ deferFetch = false }) => {
  const navigate = useNavigate();
  const [monthKey, setMonthKey] = useState(currentMonthKey);
  const [monthIls, setMonthIls] = useState(0);
  const [yearIls, setYearIls] = useState(0);
  const [year, setYear] = useState(() => Number(currentMonthKey().slice(0, 4)));
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  /** After first deferred boot, month changes fetch immediately */
  const [bootReady, setBootReady] = useState(!deferFetch);

  const openMonthDetails = () => {
    navigate(`/p/dashboard/hr-commissions?month=${encodeURIComponent(monthKey)}`);
  };

  useEffect(() => {
    if (!deferFetch || bootReady) return undefined;
    const timer = setTimeout(() => setBootReady(true), HR_COMMISSION_DEFER_MS);
    return () => clearTimeout(timer);
  }, [deferFetch, bootReady]);

  useEffect(() => {
    if (!bootReady) return undefined;
    let cancelled = false;
    setLoading(true);
    setFetchError(false);
    getHrCommissionSystemTotal({ month: monthKey })
      .then((res) => {
        if (cancelled) return;
        const data = res?.data || {};
        const ok = data.success !== false && res?.status !== 404 && res?.status !== 500;
        if (!ok && res?.status >= 400) {
          setFetchError(true);
        }
        // Client catch returns success:false with zeros — treat as error when no year key
        if (data.success === false && data.year == null && data.month == null) {
          setFetchError(true);
        }
        setMonthIls(Number(data.monthIls) || 0);
        setYearIls(Number(data.yearIls) || 0);
        setYear(
          Number(data.year) || Number(String(monthKey).slice(0, 4)) || new Date().getFullYear()
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [monthKey, bootReady]);

  const isCurrentMonth = monthKey === currentMonthKey();

  return (
    <article
      className="dashboard-stats__card dashboard-stats__card--teal dashboard-stats__card--hr dashboard-stats__card--hr-clickable"
      role="button"
      tabIndex={0}
      onClick={openMonthDetails}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openMonthDetails();
        }
      }}
      aria-label={`פתיחת פירוט עמלות HR ל${formatMonthLabel(monthKey)}`}
      title="לחץ לפירוט הזמנות החודש"
    >
      <div className="dashboard-stats__card-header">
        <span className="dashboard-stats__label">עמלות HR</span>
        <span className="dashboard-stats__icon" aria-hidden>
          <HiOutlineBanknotes />
        </span>
      </div>

      <div
        className="dashboard-stats__hr-month-nav"
        dir="rtl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="dashboard-stats__hr-nav-btn"
          onClick={() => setMonthKey((prev) => shiftMonthKey(prev, 1))}
          disabled={isCurrentMonth || loading}
          aria-label="חודש הבא"
        >
          <HiOutlineChevronRight />
        </button>
        <button
          type="button"
          className="dashboard-stats__hr-month-label dashboard-stats__hr-month-label--btn"
          onClick={openMonthDetails}
          aria-live="polite"
        >
          {formatMonthLabel(monthKey)}
        </button>
        <button
          type="button"
          className="dashboard-stats__hr-nav-btn"
          onClick={() => setMonthKey((prev) => shiftMonthKey(prev, -1))}
          disabled={loading}
          aria-label="חודש קודם"
        >
          <HiOutlineChevronLeft />
        </button>
      </div>

      <div className="dashboard-stats__hr-totals">
        <div className="dashboard-stats__hr-total dashboard-stats__hr-total--month">
          <span className="dashboard-stats__hr-total-label">חודש נבחר</span>
          <span className="dashboard-stats__value dashboard-stats__value--hr">
            {loading ? "—" : formatCurrencyIls(monthIls)}
          </span>
        </div>
        <div className="dashboard-stats__hr-total dashboard-stats__hr-total--year">
          <span className="dashboard-stats__hr-total-label">סה״כ {year}</span>
          <span className="dashboard-stats__hr-year-value">
            {loading ? "—" : formatCurrencyIls(yearIls)}
          </span>
        </div>
      </div>
      <p className="dashboard-stats__hr-hint">לחץ לפירוט הזמנות</p>
      {fetchError ? (
        <p className="dashboard-stats__hr-error" role="status">
          לא ניתן לטעון עמלות HR — ודא שה־BE מעודכן ורץ מחדש
        </p>
      ) : null}
    </article>
  );
};

const DashboardStats = ({
  stats,
  loading = false,
  homeWalletSnapshot = null,
  deferHrCommission = false,
}) => {
  const showHrCommissionKpi = canViewHrCommissionSystemKpi();
  const [hrSlot, setHrSlot] = useState(readHrCommissionKpiSlot);
  const availableFromWallet = homeWalletSnapshot?.walletSummary?.availableTotal;
  const availableCardValue =
    availableFromWallet != null
      ? availableFromWallet
      : stats?.availableProfit != null
        ? stats.availableProfit
        : null;

  useEffect(() => {
    if (!showHrCommissionKpi) return undefined;
    const onKeyDown = (event) => {
      const key = String(event.key || "").toLowerCase();
      if (key !== "s") return;
      if (!event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) return;
      // Skip while typing in form fields so Shift+S can still enter uppercase S.
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          target.closest("input, textarea, select, [contenteditable='true']"))
      ) {
        return;
      }
      event.preventDefault();
      setHrSlot((prev) => {
        const next = prev === "hr" ? "openOrders" : "hr";
        writeHrCommissionKpiSlot(next);
        return next;
      });
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showHrCommissionKpi]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (availableCardValue == null) return;
    console.log("[HOME_AVAILABLE_CARD_TRACE]", {
      componentName: "DashboardStats",
      valueProp: stats?.availableProfit,
      possibleProfit: homeWalletSnapshot?.possibleProfit,
      availableTotal: homeWalletSnapshot?.walletSummary?.availableTotal,
      walletSummary: homeWalletSnapshot?.walletSummary,
      availableBuckets: homeWalletSnapshot?.availableBuckets,
      availableRowsTotal: homeWalletSnapshot?.availableRowsTotal,
      cardDisplayedValue: availableCardValue,
    });
  }, [
    stats?.availableProfit,
    homeWalletSnapshot,
    availableCardValue,
  ]);

  useEffect(() => {
    if (import.meta.env.DEV) {
      const formatCurrency = (value) =>
        value == null
          ? "—"
          : new Intl.NumberFormat("he-IL", {
              style: "currency",
              currency: "ILS",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(value);
      const formatCount = (value) =>
        value == null ? "—" : new Intl.NumberFormat("he-IL").format(value);
      console.log("[DASHBOARD_STATS_RENDER_TRACE]", {
        futureProfit: stats?.futureProfit,
        availableProfit: stats?.availableProfit,
        historyProfit: stats?.historyProfit,
        pendingOrders: stats?.pendingOrders,
        cancelledOrders: stats?.cancelledOrders,
        openOrders: stats?.openOrders,
        showHrCommissionKpi,
        hrSlot,
        note: showHrCommissionKpi
          ? hrSlot === "hr"
            ? "Fifth card: עמלות HR with month picker + year total"
            : "Fifth card: openOrders (ביטולים / הזמנות פתוחות) — Shift+S toggles to HR"
          : "Fifth card: openOrders (ביטולים / הזמנות פתוחות)",
        loading,
        finalRenderedFutureProfit: formatCurrency(stats?.futureProfit),
        finalRenderedAvailableProfit: formatCurrency(availableCardValue),
        finalRenderedPendingOrders: formatCount(stats?.pendingOrders),
        finalRenderedOpenOrders: formatCount(stats?.openOrders),
      });
      console.log("[AVAILABLE_CARD_BINDING_TRACE]", {
        phase: "DashboardStats.render",
        cardDisplayedValue: availableCardValue,
        note: "ירוק משתמש ב-availableCardValue (לא stats.availableProfit בלבד)",
      });
    }
    logFutureCommissionFeTrace({
      source: "DashboardStats.render",
      rawResponse: null,
      mappedValue: null,
      displayedValue: {
        futureProfit: stats?.futureProfit,
        availableProfit: stats?.availableProfit,
        historyProfit: stats?.historyProfit,
      },
      stateValue: {
        futureProfit: stats?.futureProfit,
        availableProfit: stats?.availableProfit,
        historyProfit: stats?.historyProfit,
      },
      fallbackReason: loading ? "loading" : null,
    });
  }, [stats, loading, homeWalletSnapshot, showHrCommissionKpi, hrSlot, availableCardValue]);

  const showOpenOrdersCard =
    !showHrCommissionKpi || hrSlot === "openOrders";
  const showHrCard = showHrCommissionKpi && hrSlot === "hr";

  const statItems = [
    {
      id: "future-profit",
      label: "רווח עתידי",
      icon: HiOutlineCalendarDays,
      value: stats?.futureProfit,
      change: stats?.futureProfitChange,
      color: "blue",
      isCurrency: true,
    },
    {
      id: "available-profit",
      label: "רווח זמין",
      icon: HiOutlineCurrencyDollar,
      value: availableCardValue,
      change: stats?.availableProfitChange,
      color: "green",
      isCurrency: true,
    },
    {
      id: "history-profit",
      label: "סה״כ עמלות שנמשכו",
      icon: HiOutlineArchiveBox,
      value: stats?.historyProfit,
      change: stats?.historyProfitChange,
      color: "slate",
      isCurrency: true,
    },
    {
      id: "pending-orders",
      label: "הזמנות ממתינות",
      icon: HiOutlineClock,
      value: stats?.pendingOrders,
      change: stats?.pendingOrdersChange,
      color: "orange",
    },
  ];

  if (showOpenOrdersCard) {
    statItems.push({
      id: "open-orders",
      label: "ביטולים / הזמנות פתוחות",
      icon: HiOutlineXCircle,
      value: stats?.openOrders,
      change: stats?.openOrdersChange,
      color: "red",
    });
  }

  const formatValue = (value, isCurrency = false) => {
    if (value == null) return "—";
    if (isCurrency) {
      return formatCurrencyIls(value);
    }
    return new Intl.NumberFormat("he-IL").format(value);
  };

  return (
    <section className="dashboard-stats" dir="rtl" aria-label="מדדי ביצוע">
      <div className="dashboard-stats__grid">
        {statItems.map((item) => {
          const Icon = item.icon;
          const changeValue = item.change?.value ?? 0;
          const changeType = item.change?.type ?? "neutral";
          const itemLoading = item.value == null;
          const hasChange = changeValue !== 0 && !itemLoading;

          return (
            <article
              key={item.id}
              className={`dashboard-stats__card dashboard-stats__card--${item.color}`}
            >
              <div className="dashboard-stats__card-header">
                <span className="dashboard-stats__label">{item.label}</span>
                <span className="dashboard-stats__icon" aria-hidden>
                  <Icon />
                </span>
              </div>
              <div className="dashboard-stats__value">
                {formatValue(item.value, item.isCurrency)}
              </div>
              {hasChange && (
                <div
                  className={`dashboard-stats__change dashboard-stats__change--${changeType}`}
                >
                  <span className="dashboard-stats__change-icon">
                    {changeType === "positive" ? "↑" : "↓"}
                  </span>
                  <span className="dashboard-stats__change-text">
                    {Math.abs(changeValue)}% מול תקופה קודמת
                  </span>
                </div>
              )}
            </article>
          );
        })}
        {showHrCard ? (
          <HrCommissionKpiCard deferFetch={deferHrCommission} />
        ) : null}
      </div>
    </section>
  );
};

export default DashboardStats;
